"use client";

import { useMemo, useRef, useState } from "react";
import {
  type ConditionalFieldRules,
  type Timeout,
  useInitializedEffect,
  useOnce,
} from "@huub-web/ui";

import { useTemplates } from "./useTemplates";
import { isConditionalIf } from "../utils";

/**
 * Hook for checking the visibility of an input field based on the provided rules.
 * @param rules - The rules to check the visibility against.
 * @returns An object containing the visibility state of the input field.
 */
export const useVisibleIf = (rules?: ConditionalFieldRules) => {
  const [isNew, setIsNew] = useState(false);
  const { dataFieldValues } = useTemplates();

  const isVisibleState = useMemo(() => {
    const result = isConditionalIf(rules, dataFieldValues);
    // console.log("[useVisibleIf] result", result, rules);
    return result;
  }, [rules, dataFieldValues]);

  const isNewTimeoutRef = useRef<Timeout | null>(null);

  useInitializedEffect(() => {
    if (isNew) {
      isNewTimeoutRef.current = setTimeout(() => {
        setIsNew(false);
      }, 500);
    }

    return () => {
      if (isNewTimeoutRef.current) {
        clearTimeout(isNewTimeoutRef.current);
      }
    };
  }, [isNew]);

  const isNewLockedTimeoutRef = useRef<Timeout | null>(null);
  const isNewLocked = useRef(true);

  useOnce(() => {
    isNewLockedTimeoutRef.current = setTimeout(() => {
      isNewLocked.current = false;
    }, 1000);

    return () => {
      if (isNewLockedTimeoutRef.current) {
        clearTimeout(isNewLockedTimeoutRef.current);
      }
    };
  });

  useInitializedEffect(() => {
    if (isVisibleState && !isNewLocked.current) {
      setIsNew(true);
    }
  }, [isVisibleState]);

  return { isVisible: isVisibleState, isNew };
};
